import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Por qué no funciona mi antidepresivo?",
    answer:
      "Un antidepresivo puede no funcionar porque la dosis era demasiado baja, el ensayo fue demasiado corto (la mayoría necesita de 6 a 8 semanas a una dosis terapéutica), la clase de medicación no era la adecuada para su biología, o el diagnóstico necesita reevaluación. La depresión bipolar, las enfermedades no tratadas y el consumo de sustancias pueden imitar la resistencia al tratamiento. Un psiquiatra puede revisar si sus ensayos fueron adecuados y si el diagnóstico es correcto.",
  },
  {
    question:
      "¿Cuánto tiempo debo dar a un antidepresivo antes de decidir que no funciona?",
    answer:
      "La mayoría de los antidepresivos necesitan de 6 a 8 semanas a una dosis terapéutica para mostrar su efecto completo. Una mejoría parcial a las 4 semanas es común. Si no hay mejoría tras 8 semanas a una dosis adecuada, o si los efectos secundarios impiden alcanzar una dosis adecuada, un psiquiatra puede recomendar cambiar, aumentar o probar un tratamiento sin medicación.",
  },
  {
    question:
      "¿Puede la depresión volverse resistente a la medicación con el tiempo?",
    answer:
      "La depresión puede volverse menos responsiva a una medicación que antes funcionaba — a veces se llama taquifilaxia o 'efecto poop-out'. También puede recurrir tras un periodo de remisión. Si una medicación que antes funcionaba deja de hacerlo, un psiquiatra puede ajustar la dosis, cambiar a otra medicación o añadir un tratamiento sin medicación como TMS o SAINT®.",
  },
  {
    question:
      "¿Cuáles son los signos de la depresión resistente al tratamiento?",
    answer:
      "El signo principal son los síntomas depresivos persistentes tras dos o más ensayos adecuados de antidepresivos. Otros signos incluyen respuesta parcial que se estanca, episodios recurrentes a pesar de la medicación o efectos secundarios que impiden una dosificación adecuada. Un psiquiatra confirma el diagnóstico revisando el historial de medicación, dosis, duraciones y detectando condiciones que pueden imitar la resistencia.",
  },
];

export const esWhyAntidepressantNotWorking = makeEsArticle({
  slug: "why-isnt-my-antidepressant-working",
  title: "¿Por qué no funciona mi antidepresivo? | Neuro Wellness",
  description:
    "Si su antidepresivo no funciona, puede deberse a la dosis, la duración, a la clase equivocada o a un diagnóstico que necesita reevaluación. Conozca qué hacer a continuación.",
  h1: "¿Por qué no funciona mi antidepresivo?",
  breadcrumbLabel: "¿Por qué no funciona mi antidepresivo?",
  shortAnswer:
    "Un antidepresivo puede no funcionar porque la dosis era demasiado baja, el ensayo fue demasiado corto (la mayoría necesita de 6 a 8 semanas a una dosis terapéutica), la clase de medicación no era la adecuada para su biología, o el diagnóstico necesita reevaluación. La depresión bipolar, las enfermedades no tratadas y el consumo de sustancias pueden imitar la resistencia al tratamiento. Un psiquiatra puede revisar si sus ensayos fueron adecuados y si el diagnóstico es correcto.",
  keyTakeaways: [
    "La mayoría de los antidepresivos necesitan de 6 a 8 semanas a una dosis terapéutica para mostrar su efecto completo.",
    "Un ensayo inadecuado (dosis demasiado baja o demasiado corto) no es lo mismo que un ensayo fallido.",
    "La depresión bipolar, las afecciones tiroideas y el consumo de sustancias pueden imitar la resistencia al tratamiento.",
    "Tras dos ensayos adecuados, un psiquiatra puede recomendar TMS, SAINT® o SPRAVATO®.",
  ],
  sections: [
    {
      heading: "Razones comunes por las que un antidepresivo no funciona",
      paragraphs: [
        "La razón más común es un ensayo inadecuado: la dosis era demasiado baja o la medicación se suspendió antes de las 6 a 8 semanas. Otras razones incluyen una clase de medicación equivocada para su biología, un diagnóstico incompleto (como depresión bipolar diagnosticada por error como depresión unipolar), enfermedades no tratadas como el hipotiroidismo y el consumo de sustancias. Un psiquiatra revisa cada una de estas antes de concluir que una medicación ha fracasado realmente.",
      ],
    },
    {
      heading: "¿Fue adecuado su ensayo?",
      paragraphs: [
        "Un ensayo adecuado generalmente significa tomar un antidepresivo a una dosis terapéutica — no solo una dosis inicial — de forma constante durante 6 a 8 semanas. Una medicación suspendida precozmente por efectos secundarios, o que nunca se aumentó por encima de la dosis inicial, suele considerarse un ensayo incompleto más que un ensayo fallido. Revisar los registros de farmacia con un psiquiatra es el primer paso.",
      ],
    },
    {
      heading: "¿Podría ser distinto el diagnóstico?",
      paragraphs: [
        "Los episodios depresivos en el trastorno bipolar a menudo responden mal a los antidepresivos solos y pueden confundirse con depresión unipolar resistente. Las comorbilidades no tratadas — ansiedad, TEPT, TDAH, consumo de sustancias — y las enfermedades como el hipotiroidismo o la apnea del sueño también pueden producir la apariencia de resistencia. Una reevaluación cuidadosa a veces cambia el diagnóstico o revela una causa corregible.",
      ],
    },
    {
      heading: "Qué hacer a continuación",
      paragraphs: [
        "Pregunte a su psiquiatra: ¿Fueron realmente adecuados mis ensayos? ¿Debería probar una clase distinta o un aumento? ¿Soy candidato para TMS, SAINT® o SPRAVATO®? ¿Qué cubre mi seguro?",
        "[[Vea nuestra guía completa sobre cuándo el tratamiento no funciona|/es/depression-treatment-not-working]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation: "Rush AJ, et al. STAR*D report. Am J Psychiatry. 2006.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
